/**
 * @file index.tsx
 *
 * @fileoverview Renders an embedded video.
 */
import React from 'react';
import { LinkItem } from '~/types/types';
import { extractServiceFromUrl } from '~/jobiqo/utils/links';

interface EmbeddedVideoProps {
  link: LinkItem;
  height?: number | null;
  width?: number | null;
  aspectRatio?: string | null;
}

export const EmbeddedVideo = ({
  link,
  height,
  width,
  aspectRatio,
}: EmbeddedVideoProps) => {
  if (!link.url) {
    return null;
  }

  // Transform the url based on the service (YouTube or Vimeo).
  let url = link.url;
  const service = extractServiceFromUrl(url);
  if (['youtube', 'youtu'].includes(service)) {
    const baseYoutubeUrl = 'https://www.youtube.com/embed/';
    // Handle standard YouTube URLs
    const queryString = new URLSearchParams(link.url.split('?')[1]);
    // Use directly 'v' parameter as link url can contain other parameters that can break the video.
    let extractedVideoId = queryString.get('v');
    if (!extractedVideoId) {
      // Handle youtu.be URLs
      const youtubeShortRegex = /youtu\.be\/([^?]+)/;
      const match = link.url.match(youtubeShortRegex);
      extractedVideoId = match ? match[1] : null;
    }
    url = baseYoutubeUrl + extractedVideoId;
  } else if (service === 'vimeo') {
    const baseVimeoUrl = 'https://player.vimeo.com/video/';
    // Extract Vimeo video ID using regex to handle different URL formats
    const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|video\/|)(\d+)(?:$|\/|\?))|(?:player\.vimeo\.com\/video\/(\d+)(?:$|\?))/;
    const match = link.url.match(vimeoRegex);
    let extractedVideoId = match ? match[1] || match[2] : null;

    let hashParam = '';
    if (extractedVideoId) {
      // Check if the original URL has a hash parameter (h=xxx) for private
      // videos and append it to the embed URL.
      let hashMatch = link.url.match(/[?&]h=([^&]+)/)?.[1];
      // If no hash parameter is found, check if the URL contains a hash at the
      // end (e.g., https://vimeo.com/[videoId]/[hashParam])
      if (!hashMatch) {
        // Regex to capture video ID and hash at the end
        const vimeoRegex = /vimeo\.com\/(?:.*\/)?(\d+)(?:\/([a-zA-Z0-9]+))?$/;
        const match = link.url.match(vimeoRegex);
        const extractedVideoIdNew = match ? match[1] : null;
        // Check if the extracted video ID by new regex matches the initially
        // extracted video ID and if so, use the hash from the new regex
        hashMatch =
          match && extractedVideoIdNew == extractedVideoId ? match[2] : '';
      }

      hashParam = hashMatch ? `?h=${hashMatch}` : '';
    }
    url = baseVimeoUrl + extractedVideoId + hashParam;
  }

  return (
    <iframe
      width={width ?? '100%'}
      height={height ?? '100%'}
      style={{
        aspectRatio: aspectRatio ?? '16/9',
        width: width ?? '100%',
        height: height ?? '100%',
        border: '0',
      }}
      src={url}
      allowFullScreen={true}
    />
  );
};
export default EmbeddedVideo;
